/**
 * The card's reading of a permission request: the question it asks for an
 * action, the reply a request takes and the shape the card sends through
 * `answer(formId, reply)`. The pending approvals themselves come from the
 * client's snapshot (`PendingApproval` in
 * `src/lib/code-sessions/local-agent/consent.ts`).
 */
import type { CodeFormAnswer } from '@/data/code-sessions/types';
import type { PendingApproval } from '@/lib/code-sessions/local-agent/consent';

import { dialogLines } from '@/lib/code-sessions/local-agent/dialog-text';

export type { PendingApproval };
/** The three answers a permission request takes, as the client declares them. */
export type { ConsentDecision } from '@/lib/code-sessions/local-agent/consent';
/** Whether a request takes Deny alone, as the client decides it: a shell call it could not read. */
export { isCommandUnreadable } from '@/lib/code-sessions/local-agent/approval-replies';

/**
 * Whether an id names a permission request, not a form: OpenCode 2.0.26 starts every permission
 * request's id with `per` (`Permission.ID`) and gives a form `frm_`. A form's fields are named by
 * whoever asks, so a question with a field named `permission` is answered as the question it is.
 */
function isPermissionRequestId(id: string): boolean {
  return id.startsWith('per');
}

/** A permission answer, as the card sends it through `answer(formId, reply)`. */
export function isPermissionAnswer(
  reply: CodeFormAnswer,
): reply is Extract<CodeFormAnswer, { permission: unknown }> {
  return 'permission' in reply && typeof reply.permission === 'string';
}

/**
 * Whether `answer(formId, reply)` answers a permission request: the card's answer to one, not a
 * question that happens to have a field named `permission`, in a cloud session as on this computer.
 */
export function isPermissionReply(
  formId: string,
  reply: CodeFormAnswer,
): reply is Extract<CodeFormAnswer, { permission: unknown }> {
  return isPermissionRequestId(formId) && isPermissionAnswer(reply);
}

const DURATION_UNITS = [
  { ms: 3_600_000, one: 'hour', many: 'hours' },
  { ms: 60_000, one: 'minute', many: 'minutes' },
  { ms: 1_000, one: 'second', many: 'seconds' },
] as const;

/** A time limit in plain words, in the largest unit it fills whole: "10 minutes", "90 seconds". */
function durationWords(ms: number): string {
  const unit = DURATION_UNITS.find((candidate) => ms % candidate.ms === 0);
  if (unit === undefined) {
    return `${Number((ms / 1_000).toFixed(3))} seconds`;
  }
  const count = ms / unit.ms;
  return `${count} ${count === 1 ? unit.one : unit.many}`;
}

/**
 * The card's line for a command that keeps running in the background: until
 * the time limit its call sets, or with none, OpenCode's default for one. The
 * consent guard keeps a limit only above 0, which OpenCode reads as none.
 */
export function backgroundLine(timeoutMs: number | undefined): string {
  return timeoutMs === undefined
    ? 'Keeps running in the background after it starts, with no time limit.'
    : `Keeps running in the background after it starts, for up to ${durationWords(timeoutMs)}.`;
}

/** The question for a request's action: a command to run, a folder to reach, or anything else by name. */
export function approvalQuestion(action: string): string {
  if (action === 'shell') {
    return 'Allow the agent to run this shell command?';
  }
  if (action === 'external_directory') {
    return 'Allow the agent to use this location outside the session folder?';
  }
  return `Allow the agent's ${dialogLines(action).join(' ')} request?`;
}
